import { Injectable, NgZone } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { VideoCallService } from './VideoCall.service';
import { environment } from '../../../../../environments/environment';
export interface TranscriptionUpdate {
  type: 'interim' | 'final' | 'manual';
  accumulated_text: string;
  interim_text: string;
  new_segment: string;
  timestamp: number;
  seq?: number;
  status?: string;
  message?: string;
  error_details?: any;
  // MULTILANG-START
  accumulated_translation?: string;
  interim_translation?: string;
  new_translation?: string;
  full_transcript?: string;
  full_translation?: string;
  // MULTILANG-END
}

export interface FinalTranscriptPayload {
  text: string;
  // MULTILANG-START
  translation?: string;
  // MULTILANG-END
  appointmentId?: string;
  visitId?: string;
  doctorId?: string;
  patientRegId?: string;
  roomId?: string;
  timestamp?: string;
  meta?: Record<string, any>;
}

@Injectable({
  providedIn: 'root'
})
export class AudioSpeechService {
  private socket: Socket;
  private audioContext: AudioContext;
  private recorder: any;
  private stream: MediaStream;
  private remoteStreamSubscription: Subscription;
  private mixer: GainNode;
  private localInput: MediaStreamAudioSourceNode;
  private remoteInput: MediaStreamAudioSourceNode;

  private transcriptionSubject = new Subject<TranscriptionUpdate>();
  public transcription$: Observable<TranscriptionUpdate> = this.transcriptionSubject.asObservable();

  public isRecordingSubject = new BehaviorSubject<boolean>(false);
  public isRecording$: Observable<boolean> = this.isRecordingSubject.asObservable();

  public sessionStatusSubject = new BehaviorSubject<'disconnected' | 'initializing' | 'connected'>('disconnected');
  public sessionStatus$: Observable<'disconnected' | 'initializing' | 'connected'> = this.sessionStatusSubject.asObservable();

  private activitySubject = new Subject<void>();
  public activity$: Observable<void> = this.activitySubject.asObservable();

  private medicalDataSubject = new Subject<any>();
  public medicalData$: Observable<any> = this.medicalDataSubject.asObservable();

  private aiCompleteSubject = new Subject<any>();
  public aiComplete$: Observable<any> = this.aiCompleteSubject.asObservable();

  // Debug poller for Network Tab inspector
  private debugPollingInterval: any;
  private _finalApiStarted: boolean = false;

  private _activeSessionId: string = sessionStorage.getItem('active_convo_session_id') || '';

  private startPolling() {
    if (!this.debugPollingInterval) {
      this.debugPollingInterval = setInterval(() => {
        this.fetchAndPushLatestAISuggestions();
      }, 5000);
      this.fetchAndPushLatestAISuggestions();
    }
  }

  private stopPolling() {
    if (this.debugPollingInterval) {
      clearInterval(this.debugPollingInterval);
      this.debugPollingInterval = null;
    }
    sessionStorage.removeItem('active_convo_session_id');
  }

  private savedAISuggestions: any[] = null;

  public get sessionId(): string {
    return this._activeSessionId || this.socket?.id || '';
  }

  public saveAISuggestions(sections: any[]) {
    this.savedAISuggestions = JSON.parse(JSON.stringify(sections));
  }

  public getSavedAISuggestions() {
    return this.savedAISuggestions;
  }

  constructor(private videoCallService: VideoCallService, private zone: NgZone) {
  
  }

  async socketWeb(){
      this.socket = io(`${environment.convoAIUrl}/`, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000
    });

    this.socket.on('connect', () => {
      // console.log('🔌 [DIAG-STAGE-5] Socket.IO CONNECTED. Socket ID:', this.socket.id);
      if (this.isRecordingSubject.value) {
        this._activeSessionId = this.socket.id;
        sessionStorage.setItem('active_convo_session_id', this._activeSessionId);
      }
    });

    if (this._activeSessionId) {
      this.startPolling();
    }


    this.socket.on('transcription_update', (data: TranscriptionUpdate) => {
      // console.log('📝 [DIAG-STAGE-12] Frontend received transcription_update:', data.type, 'seg:', data.new_segment || data.interim_text || '');
      this.zone.run(() => {
        this.transcriptionSubject.next(data);
      });
    });

    this.socket.on('medical_form_data', (data: any) => {
      // console.log('=== MEDICAL_FORM_DATA SOCKET EVENT ===');
      // console.log('Full payload:', data);
      // console.log('group_result:', data?.group_result);
      // console.log('raw_data:', data?.raw_data);
      // console.log('ai_complete:', data?.ai_complete);
      // console.log('full_transcript:', data?.full_transcript);
      // console.log('full_translation:', data?.full_translation);

      this.zone.run(() => {
        this.medicalDataSubject.next(data);
      });
    });

    this.socket.on('ai_processing_complete', (data: any) => {
      this.zone.run(() => {
        this.stopPolling();

        // Final one-time fetch of the CLEANED data for DB-readiness
        this.fetchAndPushFinalAIData();

        this.aiCompleteSubject.next(data);
      });
    });

    this.socket.on('eos_sent_signal', (data: any) => {
      // console.log('📡 [DIAG-STAGE-5] Socket eos_sent_signal received:', data);
      this.zone.run(() => {
        // Automatically trigger /get_final_ai right after EOS is sent.
        // The REST call will block/wait on the backend until it's completely ready.
        this.fetchAndPushFinalAIData();
        
        // Forward the EOS signal to aiCompleteSubject so the UI (ehr.component.ts) can show the notification
        this.aiCompleteSubject.next(data);
      });
    });

    this.socket.on('status', (data: any) => {
      // console.log('📡 [DIAG-STAGE-5] Socket status event received:', data);
      if (data && data.status) {
        this.zone.run(() => {
          if (data.status === 'initializing') {
            this.sessionStatusSubject.next('initializing');
          } else if (data.status === 'connected') {
            this.sessionStatusSubject.next('connected');
          } else if (data.status === 'stopped' || data.status === 'disconnected' || data.status === 'error') {
            this.sessionStatusSubject.next('disconnected');
            if (this.isRecordingSubject.value) {
              this.isRecordingSubject.next(false);
              this.stopStreaming();
            }
          }
        });
      }
    });

    this.socket.on('disconnect', (reason) => {
      // console.warn('🔌 [DIAG-STAGE-5] Socket.IO DISCONNECTED. Reason:', reason);
      this.zone.run(() => {
        this.sessionStatusSubject.next('disconnected');
      });
    });

    this.socket.on('error', (err) => {
      // console.error('🔌 [DIAG-STAGE-5] Socket.IO ERROR:', err);
    });
  }

  // MULTILANG-START (Added language parameter)
  async startStreaming(language: string = 'en-IN', appointmentId?: string, doctorId?: string) {
  // MULTILANG-END
    if (this.isRecordingSubject.value) return;

    try {
      // console.log('🎤 [DIAG-STAGE-1] Requesting getUserMedia audio permission...');
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
        },
      });
      // console.log('🎤 [DIAG-STAGE-1] Microphone permission GRANTED.');
      // console.log('🎤 [DIAG-STAGE-2] MediaStream obtained, stream ID:', this.stream?.id);
      this.stream.getAudioTracks().forEach((track, i) => {
        // console.log(`🎤 [DIAG-STAGE-2] AudioTrack #${i}: label="${track.label}", enabled=${track.enabled}, muted=${track.muted}, readyState="${track.readyState}"`);
      });

      const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContextClass({ sampleRate: 16000 });

      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Load the AudioWorklet processor
      try {
        await this.audioContext.audioWorklet.addModule('assets/pcm-processor.js');
      } catch (e) {
        // console.error('Failed to load AudioWorklet module:', e);
        throw e;
      }

      this.mixer = this.audioContext.createGain();
      this.mixer.gain.value = 1.0; // Standard gain

      this.localInput = this.audioContext.createMediaStreamSource(this.stream);
      this.localInput.connect(this.mixer);

      this.remoteStreamSubscription = this.videoCallService.remoteStream$.subscribe(remoteStream => {
        if (remoteStream && remoteStream.getAudioTracks().length > 0) {
          if (this.remoteInput) {
            this.remoteInput.disconnect();
          }
          this.remoteInput = this.audioContext.createMediaStreamSource(remoteStream);
          this.remoteInput.connect(this.mixer);
        } else if (this.remoteInput) {
          this.remoteInput.disconnect();
          this.remoteInput = null;
        }
      });

      const processor = new AudioWorkletNode(this.audioContext, 'pcm-processor');

      this.mixer.connect(processor);
      processor.connect(this.audioContext.destination);

      let chunkCount = 0;
      processor.port.onmessage = (event) => {
        const pcmData = event.data;
        chunkCount++;
        const byteLen = pcmData?.byteLength || pcmData?.length || 0;

        if (chunkCount % 50 === 1) {
          // console.log(`🎤 [DIAG-STAGE-3/4] Chunk #${chunkCount} generated, byte length: ${byteLen}`);
        }

        if (!this.isRecordingSubject.value || !this.socket?.connected) {
          // console.warn(`🎤 [DIAG-STAGE-6] Chunk #${chunkCount} DROPPED on frontend! isRecording=${this.isRecordingSubject.value}, socket.connected=${this.socket?.connected}`);
          return;
        }

        this.activitySubject.next();
        this.socket.emit('audio_data', pcmData);

        if (chunkCount % 50 === 1) {
          // console.log(`🎤 [DIAG-STAGE-6] Chunk #${chunkCount} (${byteLen} bytes) SENT to backend via Socket.IO`);
        }
      };

      this.recorder = {
        stop: () => {
          if (this.remoteStreamSubscription) {
            this.remoteStreamSubscription.unsubscribe();
            this.remoteStreamSubscription = null;
          }
          if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
          }
          if (processor) processor.disconnect();
          if (this.localInput) this.localInput.disconnect();
          if (this.remoteInput) this.remoteInput.disconnect();
          if (this.mixer) this.mixer.disconnect();
          if (this.audioContext) this.audioContext.close();
        }
      };

      // Reset saved suggestions cache from previous sessions
      this.savedAISuggestions = null;

      this.socket.emit('start_stream', {
        sampleRate: this.audioContext.sampleRate,
        language: language,
        appointmentId: appointmentId,
        doctorId: doctorId
      });
      
      this._finalApiStarted = false;
      this._activeSessionId = this.socket.id || '';
      if (this._activeSessionId) {
        sessionStorage.setItem('active_convo_session_id', this._activeSessionId);
      }
      this.startPolling();
      this.isRecordingSubject.next(true);
      this.sessionStatusSubject.next('initializing');
    } catch (err) {
      // console.error('Error starting audio stream:', err);
      this.isRecordingSubject.next(false);
      throw err;
    }
  }


  stopStreaming() {
    this.isRecordingSubject.next(false);
    this.sessionStatusSubject.next('disconnected');
    if (this.recorder) {
      this.recorder.stop();
      this.recorder = null;
    }
    if (this.socket) {
      this.socket.emit('stop_stream');
    }
    // Intentionally not stopping polling here so it continues until backend explicitly indicates completion
  }

  clearText() {
    if (this.socket) {
      this.socket.emit('clear_text');
    }
  }

  pushManualTranscript(payload: FinalTranscriptPayload) {
    if (!payload?.text?.trim()) return;
    if (!this.socket) {
      // console.warn('Speech socket not initialized');
      return;
    }
    this.socket.emit('manual_text_update', payload);
  }

  submitTranscript(payload: FinalTranscriptPayload) {
    if (!payload?.text?.trim()) return;
    if (!this.socket) {
      // console.warn('Speech socket not initialized');
      return;
    }
    this.socket.emit('final_transcript', payload);
  }

  // MULTILANG-START
  async fetchSupportedLanguages(): Promise<any> {
    try {
      const response = await fetch(`${environment.convoAIUrl}/supported_languages`);
      const data = await response.json();
      return data;
    } catch (error) {
      // console.error('Error fetching supported languages:', error);
      return { languages: [] };
    }
  }
  // MULTILANG-END

  async fetchLatestAISuggestions() {
    const sid = this.sessionId;
    if (!sid) return null;
    try {
      const response = await fetch(`${environment.convoAIUrl}/get_latest_ai_suggestions?session_id=${sid}`);
      const data = await response.json();
      return data;
    } catch (error) {
      // console.error('Network Tab Polling Error:', error);
      return null;
    }
  }

  async fetchFinalAIData(sessionId?: string): Promise<any> {
    const sid = sessionId || this.sessionId;
    if (!sid) return null;
    try {
      const url = `${environment.convoAIUrl}/get_final_ai?session_id=${sid}`;
      const response = await fetch(url);
      if (response.ok) {
        return await response.json();
      }
      const fallbackUrl = `${environment.convoAIUrl}/get_final_ai_data?session_id=${sid}`;
      const fallbackRes = await fetch(fallbackUrl);
      return await fallbackRes.json();
    } catch (error) {
      return null;
    }
  }


  /** Polls and pushes results into the stream — fallback for missed socket events */
  async fetchAndPushLatestAISuggestions() {
    if (this._finalApiStarted) return;
    const sid = this.sessionId;
    if (!sid) return;
    try {
      const response = await fetch(`${environment.convoAIUrl}/get_latest_ai_suggestions?session_id=${sid}`);
      const data = await response.json();
      
      if (sid !== this.sessionId) {
        // console.warn(`[AI-SYNC] Ignoring stale polling response from old session: ${sid}`);
        return;
      }

      if (data) {
        if (data.error === 'final_ai_called' || data.status === 'stopped' || data.status === 'expired') {
          this.zone.run(() => {
            this.stopPolling();
          });
          return;
        }

        if (!data.error) {
          const hasGroupResult = (Array.isArray(data.group_result) && data.group_result.length > 0) ||
            (data.details && Array.isArray(data.details.group_result) && data.details.group_result.length > 0);
          const rawStatus = data.raw_data?._status?.status;
          const isComplete = data.ai_complete === true || rawStatus === 'done';
          // [AI-SYNC] rest_latest_request log removed
          if (hasGroupResult || isComplete) {
            this.zone.run(() => {
              this.medicalDataSubject.next(data);
            });
          }
          
          if (isComplete) {
            this.zone.run(() => {
              this.stopPolling();
              this.aiCompleteSubject.next({ status: 'completed', ...data });
            });
          }
        }
      }
    } catch (error) {
      // console.error('[HTTP Fallback] Poll error:', error);
    }
  }

  /** Performs a final, definitive fetch from /get_final_ai and pushes to UI */
  async fetchAndPushFinalAIData() {
    const sid = this.sessionId;
    if (!sid) return;
    this._finalApiStarted = true;
    this.stopPolling();
    const attemptFetch = async (): Promise<any> => {
      try {
        const response = await this.fetchFinalAIData(sid);
        
        if (sid !== this.sessionId) {
          // console.warn(`[AI-SYNC] Ignoring stale final response from old session: ${sid}`);
          return false;
        }

        if (response) {
          const payload = response.data || response;
          const hasGroupResult = Array.isArray(payload.group_result) && payload.group_result.length > 0;
          const hasProcessedRaw = payload.raw_data && Object.keys(payload.raw_data).some(k => k.startsWith('group_id_'));
          const rawStatus = payload.raw_data?._status?.status;
          // [AI-SYNC] rest_final_request log removed
          if (hasGroupResult || hasProcessedRaw || payload.ai_complete === true) {
            this.zone.run(() => {
              this.medicalDataSubject.next(payload);
            });
            return true;
          }
        }
      } catch (err) {
        // console.error('[HTTP Final] Error in final push:', err);
      }
      return false;
    };

    const success = await attemptFetch();
    if (!success) {
      // Retry once after 3 seconds — analytics may still be processing
      // console.log(`[AI-SYNC] rest_final_request | session=${sid} | result still starting, retrying in 3s...`);
      await new Promise(resolve => setTimeout(resolve, 3000));
      await attemptFetch();
    }
  }
}
